import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { createBannerAd } from "utils/services/bannerAd.services";

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .trim()
    .max(100, "Title must not exceed 100 characters"),
  redirectUrl: Yup.string()
    .required("Website redirect link is required")
    .trim(),
  isActive: Yup.boolean(),
  image: Yup.mixed()
    .required("Image is required")
    .test("fileSize", "Image size should be less than 5MB", (value) => {
      if (!value) return false;
      return value.size <= 5 * 1024 * 1024;
    })
    .test("fileType", "Please select a valid image file", (value) => {
      if (!value) return false;
      return value.type.startsWith("image/");
    }),
});

const NewBannerAd = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageError, setImageError] = useState("");

  const initialValues = {
    title: "",
    redirectUrl: "",
    isActive: true,
    image: null,
  };

  const handleImageChange = (event, setFieldValue, setErrors) => {
    const file = event.target.files[0];
    setImageError("");
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setImageError("Please select a valid image file");
        setFieldValue("image", null);
        setErrors({ image: "Please select a valid image file" });
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setImageError("Image size should be less than 5MB");
        setFieldValue("image", null);
        setErrors({ image: "Image size should be less than 5MB" });
        return;
      }

      setSelectedFile(file);
      setFieldValue("image", file);
      setErrors({ image: undefined });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setFieldValue("image", null);
    }
  };

  const handleFormSubmit = async (values, { setErrors }) => {
    if (!values.image || !selectedFile) {
      setImageError("Image is required");
      setErrors({ ...setErrors, image: "Image is required" });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("image", selectedFile);
      formData.append("redirectUrl", values.redirectUrl);
      formData.append("isActive", values.isActive);

      await createBannerAd(formData);
      toast.success("Banner ad created successfully");
      navigate("/admin/banner-ad");
    } catch (error) {
      toast.error(error.message || "Failed to create banner ad");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-center">
        <div className="w-full bg-white p-4 shadow-lg">
          <div>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, formikHelpers) => handleFormSubmit(values, formikHelpers)}
            >
              {({ values, setFieldValue, setErrors, errors, touched, submitCount }) => (
                <Form className="flex flex-col px-5">
                  <div
                    className="rounded-b border-t-4 border-[#99C2D8] bg-[#99c2d82f] px-4 py-3 text-teal-900 shadow-sm"
                    role="alert"
                  >
                    <div className="flex items-center">
                      <div className="py-1">
                        <svg
                          className="fill-current mr-4 h-6 w-6 text-teal-500"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold">Add Banner Ad Details</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                    <div className="mb-2">
                      <label className="fw-bold block text-gray-700">
                        Title: <span className="text-red-500">*</span>
                      </label>
                      <Field
                        type="text"
                        name="title"
                        className="w-full rounded-md border px-3 py-2"
                        placeholder="Enter Banner Ad Title"
                        maxLength={100}
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    <div className="mb-2">
                      <label className="fw-bold block text-gray-700">
                        Website Redirect Link:{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Field
                        type="url"
                        name="redirectUrl"
                        className="w-full rounded-md border px-3 py-2"
                        placeholder="https://example.com"
                      />
                      <ErrorMessage
                        name="redirectUrl"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    <div className="mb-2 sm:col-span-2">
                      <label className="fw-bold block text-gray-700">
                        Image: <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setFieldValue, setErrors)}
                        className="file:py-2 file:px-4 file:bg-blue-100 file:border file:border-blue-300 file:rounded file:text-blue-700 file:font-semibold w-full"
                      />
                      {(errors.image && (touched.image || submitCount > 0)) || imageError ? (
                        <div className="text-red-500 mt-1">
                          {errors.image || imageError}
                        </div>
                      ) : null}
                      {imagePreview && (
                        <div className="mt-4">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="h-32 w-32 object-cover rounded-lg"
                          />
                        </div>
                      )}
                    </div>

                    <div className="mb-2 sm:col-span-2">
                      <label className="fw-bold block text-gray-700 mb-2">
                        Status:
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <Field
                          type="checkbox"
                          name={values.isActive}
                          className="sr-only"
                        />
                        <div
                          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors cursor-pointer ${
                            values.isActive ? "bg-green-500" : "bg-gray-300"
                          }`}
                          onClick={() => {
                            const newValue = !values.isActive;
                            setFieldValue("isActive", newValue);
                          }}
                        >
                          <span
                            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                              values.isActive
                                ? "translate-x-7"
                                : "translate-x-1"
                            }`}
                          ></span>
                        </div>
                        <span className="ml-3 text-gray-700">
                          {values.isActive ? "Active" : "Inactive"}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="flex items-center gap-4">
                      <button
                        type="submit"
                        className="bg-theme cursor-pointer rounded-md px-5 py-2 text-white"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                      <button
                        type="button"
                        className="cursor-pointer rounded-md bg-gray-400 px-5 py-2 text-white"
                        disabled={isSubmitting}
                        onClick={() => navigate("/admin/banner-ad")}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBannerAd;

